import { format } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { TimePickerInput } from 'shared/components/ui/time-picker-input';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './time-picker.style';

const initialOptions = [
  9 * 60 * 60 * 1000, // 09:00
  12 * 60 * 60 * 1000, // 12:00
  15 * 60 * 60 * 1000, // 15:00
  18 * 60 * 60 * 1000, // 18:00
  21 * 60 * 60 * 1000, // 21:00
];

interface TimePickerProps {
  id?: string;
  options?: number[];
  onChange: (value?: number) => void;
  value?: number | null;
  timeFormat: TimeFormatOptions;
  placeholder?: string;
  icon?: React.FC;
  showAsFormInput?: boolean;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  id,
  options = initialOptions,
  onChange,
  value,
  timeFormat,
  placeholder,
  icon,
  showAsFormInput,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLUListElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const onBlur = ({
    relatedTarget,
  }:
    | React.FocusEvent<HTMLInputElement>
    | React.FocusEvent<HTMLButtonElement>) => {
    if (
      !relatedTarget ||
      !(
        containerRef.current?.contains(relatedTarget as Node) ||
        optionsListRef.current?.contains(relatedTarget as Node)
      )
    ) {
      closeMenu();
    }
  };

  const _placeHolder = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    if (!options.length) {
      return timeFormat;
    }

    const dateObj = new Date();
    dateObj.setHours(0, 0, 0, options[0]);
    return format(dateObj, timeFormat);
  }, [options, placeholder, timeFormat]);

  return (
    <Styled.Container
      ref={containerRef}
      $hasValue={isNumber(value)}
      $fullWidth={!!showAsFormInput}
    >
      <TimePickerInput
        id={id}
        onChange={onChange}
        value={value}
        timeFormat={timeFormat}
        onFocus={openMenu}
        placeholder={_placeHolder}
        onBlur={onBlur}
        icon={icon}
        showAsFormInput={showAsFormInput}
        fullWidth={showAsFormInput}
        canClearEntry
      />

      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          onOutside={closeMenu}
          position="bottom-start"
        >
          <PopupMenuList ref={optionsListRef}>
            {options.map((option) => {
              const onItem = () => {
                onChange(option);
                closeMenu();
              };

              const dateObj = new Date();
              dateObj.setHours(0, 0, 0, option);

              return (
                <PopupMenuListItem key={option}>
                  <PopupMenuButton
                    onClick={onItem}
                    onBlur={onBlur}
                    tabIndex={0}
                    type="button"
                  >
                    {format(dateObj, timeFormat)}
                  </PopupMenuButton>
                </PopupMenuListItem>
              );
            })}
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
